import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { useGetAgentsQuery } from "../../redux/api/agentsApi";
import {
  useGetAllTasksQuery,
  usePostTaskMutation,
} from "../../redux/api/tasksApi";
import AutoComplete from "../../components/FormElements/AutoComplete";
import DateAndTime from "../../components/DateAndTime";
import {
  formatLocalDateTime,
  getCurrentLocalDateTime,
} from "../../components/getDateValues";

export default function AddTask({ onClose }) {
  const currentAgency = useSelector(selectCurrentagency);
  const [postTask, { isLoading }] = usePostTaskMutation();
  const { refetch } = useGetAllTasksQuery({ agcId: currentAgency.agencyid });

  const methods = useForm();
  const { handleSubmit, control } = methods;
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const isLargeScreen = useMediaQuery("(min-width:1024px)");

  const { data: agents, isFetching } = useGetAgentsQuery(
    currentAgency.agencyid
  );

  const onSubmit = async (data) => {
    const taskReceivedDate = getCurrentLocalDateTime();

    if (!startDate || !startTime) {
      alert("Please select both a deadline date and time.");
      return;
    }

    const localISOString = formatLocalDateTime(startDate, startTime);

    const payload = {
      receiverAgent: data.agentid,
      description: data.description,
      status: 0,
      agencyId: currentAgency.agencyid,
      taskReceivedDate,
      deadLine: localISOString,
    };

    try {
      await postTask(payload).unwrap();
      refetch();
      onClose();
    } catch (err) {
      console.error("Failed to post task", err);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          p: 4,
          maxWidth: isLargeScreen ? 500 : "100%",
          margin: "0 auto",
        }}
      >
        <Typography variant="h3" mb={2}>
          Add New Task
        </Typography>

        <Controller
          name="agentid"
          control={control}
          rules={{ required: "Agent is required" }}
          render={({ field }) => (
            <AutoComplete
              {...field}
              isLoading={isFetching}
              options={
                agents
                  ?.filter((agent) => agent.status === 1) // only active agents
                  .map((agent) => ({
                    agentid: agent.agentid,
                    agentname: agent.agentname,
                  })) || []
              }
              label="Select Staff"
              optionId="agentid"
              optionLabel="agentname"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          rules={{ required: "Description is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Description"
              multiline
              rows={4}
              fullWidth
              sx={{ my: 3 }}
            />
          )}
        />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" mb={1}>
            Deadline Date
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            <DateAndTime
              startDate={startDate}
              setStartDate={setStartDate}
              startTime={startTime}
              setStartTime={setStartTime}
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ color: "white" }}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}
